import { useTerminalStore } from '../store/useTerminalStore';
import type { PanelDataState, UpstreamSource } from '../types/gateway';

/**
 * DDD #180 S1.2 — resolve a panel's data lifecycle into a tri-state (+ready).
 *
 * Precedence:
 *   1. degraded — the fetch errored (panelStatus='degraded') OR the panel's
 *                 upstream is reported down (upstream_health[source]===false).
 *                 The upstream check catches the FAIL-OPEN case: gateway returns
 *                 200-[] because the upstream is down, which would otherwise look
 *                 like a benign live-empty.
 *   2. ready    — panel has data and the upstream is healthy/unknown.
 *   3. live-empty — 200 success but no rows yet (informative "no data yet").
 *   4. loading  — request still in flight / not yet recorded.
 *
 * `source` undefined → no single upstream maps to this panel; rely on the
 * client-side fetch outcome (panelStatus) only.
 *
 * upstreamHealth absent (older gateway) → treated as unknown; no override.
 */
export function usePanelState(panelKey: string, source?: UpstreamSource): PanelDataState {
  const status = useTerminalStore((s) => s.panelStatus[panelKey]);
  const upstreamDown = useTerminalStore((s) =>
    source != null && s.upstreamHealth != null ? s.upstreamHealth[source] === false : false,
  );

  if (status === 'degraded' || upstreamDown) return 'degraded';
  if (status === 'ready') return 'ready';
  if (status === 'live-empty') return 'live-empty';
  return 'loading';
}
